<script lang="ts">
import { computed, defineComponent, inject, watch } from "vue";
import Modal from "@/components/Modal.vue";
import BeratungDirekt from "@/components/vorschlag/modals/BeratungDirekt.vue";
import VorschlagMakler from "@/components/vorschlag/modals/VorschlagMakler.vue";
import VorschlagDirekt from "@/components/vorschlag/modals/VorschlagDirekt.vue";
import AnfrageService from "@/businesslogic/services/AnfrageService";
import router from "@/router";
import cookieUtils from "@/businesslogic/utils/cookieUtils";
import Logger from "js-logger";
import BuendelDokumentLinks from "@/components/vorschlag/modals/BuendelDokumentLinks.vue";
import tracking from "@/businesslogic/tracking/tracking";
import storeUtils from "@/businesslogic/utils/storeUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

export default defineComponent({
  name: "EmailOderHerunterladen",
  emits: ["validierung-erforderlich"],
  components: {
    VorschlagDirekt,
    VorschlagMakler,
    BeratungDirekt,
    Modal,
    BuendelDokumentLinks,
  },
  props: {
    produkt: String,
    antrag: {
      type: Boolean,
      default: false,
    },
    trackingZusatz: String,
    vorschlagZugriff: String,
  },
  setup(props, { emit }) {
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const piniaStore = useFormStore();

    const { getCookie } = cookieUtils();

    const zeigeSpinner: any = inject("zeigeSpinner");
    const { istFhkVerkaufsprodukt, istVhvVerkaufsprodukt } = storeUtils();

    const beschriftung = computed(() => {
      return props.antrag ? "Antrag" : "Vorschlag";
    });

    const closeFerienhausDownload = () => {
      if (istSichtbar("modal-vorschlagMaklerDownload")) {
        wechsleSichtbarkeit("modal-vorschlagMaklerDownload");
      }
      if (istSichtbar("modal-vorschlagDirektDownload")) {
        wechsleSichtbarkeit("modal-vorschlagDirektDownload");
      }
      wechsleSichtbarkeit("modal-ferienhausDownload");
    };

    function getNettoJahresbeitrag(): number {
      let vSteuer = 19;
      let bruttobeitrag = piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].jaehrlich;

      if (piniaStore.state.verkaufsprodukt.toLowerCase() === "vgv") {
        vSteuer = 16.34;
      } else if (istVhvVerkaufsprodukt(piniaStore.state)) {
        vSteuer = 16.15;
      } else if (
        piniaStore.state.verkaufsprodukt.toLowerCase() === "bh" ||
        piniaStore.state.verkaufsprodukt.toLowerCase() === "bl"
      ) {
        bruttobeitrag = piniaStore.state.preise.produktLinien[0].einmalig;
      }

      return Math.round((bruttobeitrag / (100 + vSteuer)) * 100 * 100) / 100;
    }

    function gtmDataLayer(typ: string) {
      if (piniaStore.state.isMakler) {
        return;
      }

      const event = "gev_offer";
      const gev_conv_label = "Angebot_" + typ.toUpperCase();
      const gev_conv_uri = "Offer";

      const dataLayerInhalt = {
        event: event,
        gev_conv_cat: "Angebot",
        gev_conv_label: gev_conv_label,
        gev_conv_uri: gev_conv_uri,
        gev_conv_value_net: getNettoJahresbeitrag(),
        gev_conv_product: piniaStore.state.verkaufsprodukt,
        gev_conv_id: "",
        gev_conv_session_id: piniaStore.state.sessionId,
        gev_conv_user_id: getCookie("Vid") ? getCookie("Vid") : getCookie("vid"),
      };
      Logger.debug("GTM DataLayer.push: ", dataLayerInhalt);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(dataLayerInhalt);
    }

    const emailModalZeigen = () => {
      if (piniaStore.state.isMakler) {
        wechsleSichtbarkeit("modal-vorschlagMaklerEmail");
      } else {
        wechsleSichtbarkeit("modal-vorschlagDirekt");
        gtmDataLayer("EMAIL");
      }
    };

    const emailAnfordern = () => {
      if (sollElementarValidiertWerden()) {
        emit("validierung-erforderlich", "email");
      } else {
        emailModalZeigen();
      }
    };

    async function herunterladenDirekt() {
      zeigeSpinner.value = true;
      AnfrageService.herunterladen(
        piniaStore.getSessionToken(piniaStore.state),
        piniaStore.state,
        props.antrag,
        "EmailOderHerunterladen",
        props.trackingZusatz,
      )
        .then((res) => {
          if (res.status != 200) {
            throw new Error(res.statusText);
          }
          return res.data;
        })
        .then((data) => {
          piniaStore.state.id = data.id;
          piniaStore.state.anfrageNummer = data.anfrageNummer;
          piniaStore.state.erstelldatum = data.erstelldatum;
          const fileLink = document.createElement("a");
          fileLink.href = "data:application/pdf;base64," + data.korrespondenz.vorschlagDokument;
          fileLink.setAttribute("download", data.korrespondenz.dokumentName);
          document.body.appendChild(fileLink);

          zeigeSpinner.value = false;
          fileLink.click();
        })
        .catch(() => {
          zeigeSpinner.value = false;
          router.push("/fehler");
        });
    }

    function sollElementarValidiertWerden() {
      return (
        piniaStore.state.tarifinformationen.elementar &&
        piniaStore.state.verkaufsprodukt !== "VGV" &&
        !istFhkVerkaufsprodukt(piniaStore.state) &&
        !props.antrag
      );
    }

    watch(
      () => props.vorschlagZugriff,
      () => {
        if (props.vorschlagZugriff == "email") {
          emailModalZeigen();
        } else if (props.vorschlagZugriff == "download") {
          herunterladenMitOderOhneDialog();
        }
      },
    );

    const herunterladenMitOderOhneDialog = () => {
      if (piniaStore.state.isMakler) {
        wechsleSichtbarkeit("modal-vorschlagMaklerDownload");
      } else if (piniaStore.state.outbound.downloadAktiv) {
        const { track } = tracking();
        track(
          piniaStore.getSessionToken(piniaStore.state),
          "VorschlagDirekt",
          "download_popup_oeffnen_" + props.trackingZusatz,
          piniaStore.state,
        );
        wechsleSichtbarkeit("modal-vorschlagDirektDownload");
      } else {
        if (istFhkVerkaufsprodukt(piniaStore.state)) {
          wechsleSichtbarkeit("modal-ferienhausDownload");
        } else {
          herunterladenDirekt();
        }
        gtmDataLayer("PDF");
      }
    };

    const vorschlagHerunterladen = () => {
      if (sollElementarValidiertWerden()) {
        emit("validierung-erforderlich", "download");
      } else {
        herunterladenMitOderOhneDialog();
      }
    };

    const beratungAnfordern = () => {
      wechsleSichtbarkeit("modal-beratungDirekt");
      AnfrageService.beratungAnfordern(
        piniaStore.getSessionToken(piniaStore.state),
        piniaStore.state.isMakler ? "VorschlagMakler" : "VorschlagDirekt",
        piniaStore.state,
        "beratung_anfordern_" + props.trackingZusatz,
      );
    };
    const isSafariMobil = isSafariIOS();
    function isSafariIOS(): null | false | boolean {
      const ua = window.navigator.userAgent;
      const iOS = ua.match(/iPad/i) || ua.match(/iPhone/i);
      const webkit = ua.match(/WebKit/i);
      return iOS && webkit && !ua.match(/CriOS/i) && !ua.match(/EdgiOS/i) && !ua.match(/Chrome/i) && !ua.match(/Edg/i);
    }

    return {
      props,
      emailAnfordern,
      beratungAnfordern,
      vorschlagHerunterladen,
      wechsleSichtbarkeit,
      istSichtbar,
      piniaStore,
      beschriftung,
      closeFerienhausDownload,
      isSafariMobil,
    };
  },
});
</script>

<template>
  <hr class="u-mar-y-2" style="border: 1px solid #959595" />
  <div class="o-grid u-mar-b-3">
    <div class="o-grid__cell o-grid__cell--12 u-form-grid-nospace-y">
      <div class="o-grid o-grid--align-center">
        <div v-if="!isSafariMobil" class="o-grid__cell o-grid__cell--12-360 o-grid__cell--4-1024">
          <button
            :title="beschriftung + (piniaStore.state.isMakler === true ? '' : ' als PDF') + ' herunterladen'"
            class="c-button c-button--inverted c-button--full-width c-button--word-wrap-flex-height"
            @click="vorschlagHerunterladen"
          >
            {{ beschriftung }}
            {{ piniaStore.state.isMakler === true ? "" : " als PDF" }}
            herunterladen
          </button>
        </div>
        <div class="o-grid__cell o-grid__cell--12-360 o-grid__cell--4-1024">
          <button
            class="c-button c-button--inverted c-button--full-width c-button--word-wrap-flex-height"
            data-cy="submitVorschlagPerEmail"
            v-bind:title="beschriftung + (piniaStore.state.isMakler === true ? ' versenden' : ' per E-Mail anfordern')"
            @click="emailAnfordern"
          >
            {{ beschriftung }}
            {{ piniaStore.state.isMakler === true ? " versenden" : " per E-Mail anfordern" }}
          </button>
        </div>
        <slot name="button-3">
          <div v-if="piniaStore.state.isMakler !== true" class="o-grid__cell o-grid__cell--12-360 o-grid__cell--4-1024">
            <button
              class="c-button c-button--inverted c-button--full-width c-button--word-wrap-flex-height"
              @click="beratungAnfordern"
            >
              Ich wün­sche eine per­sön­liche Beratung
            </button>
          </div>
        </slot>
      </div>
    </div>
  </div>

  <modal
    v-if="istSichtbar('modal-beratungDirekt')"
    text="Unser Beratungsteam erstellt Ihnen gern ein individuelles Angebot. Sie erreichen uns von Montag bis Freitag von 8:00 bis 18:00 Uhr."
    titel="Jetzt Angebot anfordern"
    @closeModal="wechsleSichtbarkeit('modal-beratungDirekt')"
  >
    <template v-slot:inhalt>
      <beratung-direkt />
    </template>
  </modal>

  <!-- Modale Vorschlag per Mail, Makler-Download -->
  <modal
    v-if="istSichtbar('modal-vorschlagDirekt')"
    :text="
      'Lassen Sie sich einen ' +
      beschriftung +
      ' per E-Mail zusenden. Die E-Mail enthält neben dem ' +
      beschriftung +
      ' auch einen Link, über den Sie jederzeit in den Tarifrechner zurückkehren können.'
    "
    :titel="'Ihr ' + beschriftung + ' per E-Mail'"
    @closeModal="wechsleSichtbarkeit('modal-vorschlagDirekt')"
  >
    <template #inhalt="inhaltProps">
      <vorschlag-direkt
        :antrag="antrag"
        :is-vorschlag-email="true"
        :produkt="produkt"
        :ist-outbound-aktiv="piniaStore.state.outbound.emailAktiv"
        @closeModal="wechsleSichtbarkeit('modal-vorschlagDirekt')"
        @textVerbergen="inhaltProps.textVerbergenToggle"
      />
    </template>
  </modal>

  <!-- VorschlagMaklerDownload -->
  <modal
    v-if="istSichtbar('modal-vorschlagMaklerDownload')"
    :text="
      'Hier können Sie einen personalisierten ' +
      beschriftung +
      ' erstellen. Alle von Ihnen angegebenen Kundendaten werden im ' +
      beschriftung +
      ' berücksichtigt und für die spätere Wiederaufnahme automatisch gespeichert.'
    "
    :titel="beschriftung + ' herunterladen'"
    @closeModal="wechsleSichtbarkeit('modal-vorschlagMaklerDownload')"
  >
    <template #inhalt="inhaltProps">
      <vorschlag-makler
        :antrag="antrag"
        :isVorschlagEmail="false"
        :produkt="produkt"
        :trackingZusatz="trackingZusatz"
        @closeModal="wechsleSichtbarkeit('modal-vorschlagMaklerDownload')"
        @textVerbergen="inhaltProps.textVerbergenToggle"
      />
    </template>
  </modal>

  <!-- VorschlagDirektDownload -->
  <modal
    v-if="istSichtbar('modal-vorschlagDirektDownload')"
    :text="
      'Laden Sie hier bequem Ihren ' +
      beschriftung +
      ' als PDF herunter.' +
      ' Über den Link im PDF kommen Sie jederzeit einfach in den Tarifrechner zurück.'
    "
    :titel="beschriftung + ' herunterladen'"
    @closeModal="wechsleSichtbarkeit('modal-vorschlagDirektDownload')"
  >
    <template #inhalt="inhaltProps">
      <vorschlag-direkt
        :antrag="antrag"
        :isVorschlagEmail="false"
        :produkt="produkt"
        :trackingZusatz="trackingZusatz"
        :ist-outbound-aktiv="piniaStore.state.outbound.downloadAktiv"
        @closeModal="wechsleSichtbarkeit('modal-vorschlagDirektDownload')"
        @textVerbergen="inhaltProps.textVerbergenToggle"
      />
    </template>
  </modal>

  <!-- VorschlagMaklerEmail -->
  <modal
    v-if="istSichtbar('modal-vorschlagMaklerEmail')"
    :text="
      'Lassen Sie sich einen ' +
      beschriftung +
      ' per E-Mail zusenden. Die E-Mail enthält neben dem ' +
      beschriftung +
      ' auch einen Link, über den Sie jederzeit in den Tarifrechner zurückkehren können.'
    "
    :titel="beschriftung + ' per E-Mail versenden'"
    @closeModal="wechsleSichtbarkeit('modal-vorschlagMaklerEmail')"
  >
    <template #inhalt="inhaltProps">
      <vorschlag-makler
        :antrag="antrag"
        :isVorschlagEmail="true"
        :produkt="produkt"
        @closeModal="wechsleSichtbarkeit('modal-vorschlagMaklerEmail')"
        @textVerbergen="inhaltProps.textVerbergenToggle"
      />
    </template>
  </modal>

  <!-- Ferienhaus -->
  <modal
    v-if="istSichtbar('modal-ferienhausDownload')"
    :titel="beschriftung + ' herunterladen'"
    text="Download-Links:"
    @closeModal="closeFerienhausDownload"
  >
    <template #inhalt="inhaltProps">
      <buendel-dokument-links
        :antrag="antrag"
        :trackingZusatz="trackingZusatz"
        @textVerbergen="inhaltProps.textVerbergenToggle"
      />
    </template>
  </modal>
</template>

<style lang="scss" scoped>
.filelink {
  color: #0f5582;
  text-decoration: underline;
  cursor: pointer;
}
</style>
